import { Connection, PublicKey, ParsedAccountData } from '@solana/web3.js'
import {
  MIN_SOL_BALANCE,
  SOL_MINT,
  STATIC_QUOTE_TOKENS,
  USDC_MINT,
  WRAPPED_SOL_MINT,
} from './constants'
import { BN } from '@project-serum/anchor'
import { I80F48 } from './I80F48'
import { abbreviateAddress } from './formatting'
import {
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  AccountLayout,
} from '@solana/spl-token'
import { tryGetPubKey } from './governance/tools'
import {
  fetchTokenPairSummary,
  TokenPairSummary,
} from 'hooks/useTokenPairSummary'
import store from 'stores/store'
import { NextRouter } from 'next/router'
import { TokenMetadataItem } from 'types/token'
import { makeJupiterPriceUrlForMints } from '@components/swap/useBirdeyeSwapData'
import { JupiterPriceResponse } from '../types/jupiter'
import { jupiterFetch } from './jupiter'
import { toContextTuplesString } from './logging'
import {
  fetchBirdeyeMetadataForMints,
  fetchBirdeyePricesForMints,
} from 'apis/birdeye/tokens'

export interface TokenAccount {
  publicKey: PublicKey
  mint: PublicKey
  owner: PublicKey
  amount: number
  decimals: number
  uiAmount: number
}

//type RawNft = Nft | Sft | SftWithToken | NftWithToken
// type NftWithATA = RawNft & {
//   owner: null | PublicKey
//   tokenAccountAddress: null | PublicKey
// }

// function exists<T>(item: T | null | undefined): item is T {
//   return !!item
// }

// const enhanceNFT = (nft: NftWithATA) => {
//   return {
//     image: nft.json?.image || '',
//     name: nft.json?.name || '',
//     address: nft.metadataAddress.toBase58(),
//     collectionAddress: nft.collection?.address.toBase58(),
//     mint: nft.mint.address.toBase58(),
//     tokenAccount: nft.tokenAccountAddress?.toBase58() || '',
//     json: nft.json,
//   }
// }

// function loadNft(
//   nft: Metadata<JsonMetadata<string>> | Nft | Sft,
//   connection: Connection,
// ) {
//   const metaplex = new Metaplex(connection)

//   return safeRace([
//     metaplex
//       .nfts()
//       // eslint-disable-next-line @typescript-eslint/ban-ts-comment
//       // @ts-ignore
//       .load({ metadata: nft })
//       .catch((e) => {
//         console.error(e)
//         return null
//       }),
//   ])
// }

// export async function getNFTsByOwner(owner: PublicKey, connection: Connection) {
//   const metaplex = new Metaplex(connection)

//   const rawNfts = await metaplex.nfts().findAllByOwner({
//     owner,
//   })

//   const nfts = await Promise.all(
//     rawNfts.map((nft) => loadNft(nft, connection)),
//   ).then((nfts) =>
//     Promise.all(
//       nfts.filter(exists).map(async (nft) => ({
//         ...nft,
//         owner,
//         tokenAccountAddress: getAssociatedTokenAddressSync(
//           nft.mint.address,
//           owner,
//           true,
//         ),
//       })),
//     ),
//   )

//   return nfts.map(enhanceNFT)
// }

export async function getTokenAccountsByOwnerWithWrappedSol(
  connection: Connection,
  owner: PublicKey,
): Promise<TokenAccount[]> {
  const solReq = connection.getAccountInfo(owner)
  const tokenReq = connection.getParsedTokenAccountsByOwner(
    owner,
    { programId: TOKEN_PROGRAM_ID },
    'confirmed',
  )

  // fetch data
  const [solResp, tokenResp] = await Promise.all([solReq, tokenReq])

  // parse token accounts
  const tokenAccounts: TokenAccount[] = tokenResp.value.map((t) => {
    return {
      publicKey: t.pubkey,
      mint: t.account.data.parsed.info.mint,
      owner: t.account.data.parsed.info.owner,
      amount: t.account.data.parsed.info.tokenAmount.amount,
      uiAmount: t.account.data.parsed.info.tokenAmount.uiAmount,
      decimals: t.account.data.parsed.info.tokenAmount.decimals,
    }
  })
  // create fake wrapped sol account to reflect sol balances in user's wallet
  const lamports = solResp?.lamports || 0
  const solAccount = {
    publicKey: owner,
    mint: WRAPPED_SOL_MINT,
    owner,
    amount: lamports,
    uiAmount: toUiDecimals(lamports, 9),
    decimals: 9,
  } satisfies TokenAccount

  // prepend SOL account to beginning of list
  return [solAccount, ...tokenAccounts]
}

export const getToken2022AccountsByOwner = async (
  connection: Connection,
  walletPk: PublicKey,
): Promise<TokenAccount[]> => {
  const token2022Accounts = await connection.getTokenAccountsByOwner(walletPk, {
    programId: TOKEN_2022_PROGRAM_ID,
  })

  const accountsWithMintInfos = await Promise.all(
    token2022Accounts.value.map(async ({ pubkey, account }) => {
      const accountData = AccountLayout.decode(new Uint8Array(account.data))
      const mintAddress = new PublicKey(accountData.mint)

      const mintInfo = await connection.getParsedAccountInfo(mintAddress)
      return {
        tokenAccountPubkey: pubkey,
        accountData,
        mintAddress,
        mintInfo,
      }
    }),
  )

  return accountsWithMintInfos.map(
    ({ tokenAccountPubkey, accountData, mintAddress, mintInfo }) => {
      const rawAmount = Number(accountData.amount)
      const isParsedAccountData = (
        data: Buffer | ParsedAccountData | undefined,
      ): data is ParsedAccountData =>
        (data as ParsedAccountData)?.parsed !== undefined

      const decimals = isParsedAccountData(mintInfo.value?.data)
        ? mintInfo.value?.data.parsed.info.decimals
        : 0

      return {
        amount: rawAmount,
        decimals,
        mint: mintAddress,
        owner: walletPk,
        publicKey: tokenAccountPubkey,
        uiAmount: decimals ? toUiDecimals(rawAmount, decimals) : 0,
      } satisfies TokenAccount
    },
  )
}

export const fetchMarketDataForMint = async (
  mint: string,
): Promise<TokenPairSummary | null | undefined> => {
  try {
    return (await fetchTokenPairSummary(mint, STATIC_QUOTE_TOKENS.USDC.mint))
      ?.summary
  } catch (e) {
    console.error(`failed to fetch market data for mint: mint=${mint}`, e)
    return null
  }
}

// Thin wrapper kept for back-compat with existing call sites.
// Delegates to Birdeye via the adapter layer.
export const fetchTrafficMetadataForMints = async (
  mints: string[],
): Promise<TokenMetadataItem[]> => {
  if (mints.length === 0) {
    console.warn('No mints provided for metadata fetch')
    return []
  }
  try {
    return await fetchBirdeyeMetadataForMints(mints)
  } catch (e) {
    console.error(
      `failed to fetch metadata for mints: mints=${mints.join(',')}`,
      e,
    )
    return []
  }
}

interface ParsedTokenInfo {
  decimals: number
  freezeAuthority: string | null
  isInitialized: boolean
  mintAuthority: string | null
  supply: string
  extensions?: (ParsedExtension | MetadataExtension)[]
}

interface ParsedExtension {
  extension: string
  state: Record<string, unknown>
}

interface MetadataExtension {
  extension: 'tokenMetadata'
  state: {
    additionalMetadata?: unknown[]
    mint?: string
    name?: string
    symbol?: string
    updateAuthority?: string
    uri?: string
  }
}

export const fetchMetadataForMints = async (
  mints: string[],
  metadataCache?: Map<string, TokenMetadataItem[]>,
  connection?: Connection,
): Promise<TokenMetadataItem[]> => {
  mints = mints.filter((mint) => {
    try {
      new PublicKey(mint)
      return true
    } catch (e) {
      return false
    }
  })
  const metadataCacheKey = mints.toString()
  if (metadataCache && metadataCache.has(metadataCacheKey)) {
    return metadataCache.get(metadataCacheKey) as TokenMetadataItem[]
  }

  try {
    let meta: TokenMetadataItem[] = await fetchTrafficMetadataForMints(mints)
    meta = meta.map((x) => {
      if (x.mint === USDC_MINT && !x.latest_price_usd) {
        return {
          ...x,
          latest_price_usd: 1,
        }
      }
      return x
    })
    for (const mint of mints) {
      const existingToken = meta.find((x) => x.mint === mint)
      //fallback if token metadata in traffic is not available
      if (
        existingToken &&
        (!existingToken.latest_price_usd ||
          !existingToken.token_program ||
          !tryGetPubKey(existingToken.token_program)) &&
        connection
      ) {
        const [info, jupiterPrice] = await Promise.all([
          connection.getParsedAccountInfo(new PublicKey(mint)),
          fetchJupiterPriceForMint(mint),
        ])

        const tokenInfo: ParsedTokenInfo | undefined | null = (
          info.value?.data as ParsedAccountData
        ).parsed.info as ParsedTokenInfo | undefined | null
        const tokenWithProgramId = {
          ...existingToken,
          latest_price_usd: jupiterPrice || 0,
          token_program: tokenInfo?.extensions
            ? TOKEN_2022_PROGRAM_ID.toBase58()
            : TOKEN_PROGRAM_ID.toBase58(),
        }
        meta = [...meta.filter((x) => x.mint !== mint), tokenWithProgramId]
      }

      if (!existingToken && connection) {
        const [info, jupiterPrice] = await Promise.all([
          connection.getParsedAccountInfo(new PublicKey(mint)),
          fetchJupiterPriceForMint(mint),
        ])

        const tokenInfo = (info.value?.data as ParsedAccountData).parsed
          .info as ParsedTokenInfo | undefined | null
        const metadata = tokenInfo?.extensions?.find(
          (x) => x.extension === 'tokenMetadata',
        )?.state as MetadataExtension['state'] | undefined

        const newTokenInfo = {
          latest_price_usd: jupiterPrice || 0,
          decimals: tokenInfo?.decimals ?? 6,
          symbol: metadata?.symbol || abbreviateAddress(mint),
          name: metadata?.name || mint,
          mint: mint,
          image_url: metadata?.uri || '',
          description: '',
          twitter: '',
          coingecko_id: '',
          volume_24h_usd: 0,
          amount_24h: 0,
          trades_count_24h: 0,
          website: '',
          telegram: '',
          token_program: tokenInfo?.extensions
            ? TOKEN_2022_PROGRAM_ID.toBase58()
            : TOKEN_PROGRAM_ID.toBase58(),
        }
        meta.push(newTokenInfo)
      }
    }
    if (metadataCache) {
      metadataCache.set(metadataCacheKey, meta)
    }
    return meta
  } catch (e) {
    console.error('failed to fetch metadata for mints', e)
    return []
  }
}

export type LastPrice = {
  mint: string
  price: number
}

export const fetchLastPriceForMints = async (
  mints: string[],
): Promise<LastPrice[]> => {
  if (mints.length === 0) return []
  try {
    return await fetchBirdeyePricesForMints(mints)
  } catch (e) {
    console.error(
      `failed to fetch last price for mints: mints=${mints.join(',')}`,
      e,
    )
    return []
  }
}

export function toUiDecimals(
  nativeAmount: BN | I80F48 | number,
  decimals: number,
): number {
  // TODO: remove BN and upgrade to bigint https://github.com/solana-labs/solana/issues/27440
  if (nativeAmount instanceof BN) {
    nativeAmount = I80F48.fromU64(nativeAmount)
  }
  if (nativeAmount instanceof I80F48) {
    return nativeAmount.div(I80F48.fromNumber(10 ** decimals)).toNumber()
  }
  return nativeAmount / 10 ** decimals
}

export function toNative(uiAmount: number, decimals: number): BN {
  return new BN((uiAmount * 10 ** decimals).toFixed(0))
}

export const goToTokenPage = (
  address: string,
  router: NextRouter,
  quoteMint?: string,
  baseTokenDetails?: TokenMetadataItem,
  quoteTokenDetails?: TokenMetadataItem,
) => {
  const set = store.getState().set
  set((state) => {
    state.routeLoading = true
  })
  store
    .getState()
    .actions.setMarketAndSwapToken(
      address,
      quoteMint,
      baseTokenDetails,
      quoteTokenDetails,
    )

  router.push(`/token/${address}`).finally(() => {
    set((state) => {
      state.routeLoading = false
    })
  })
}

export const walletBalanceForToken = (
  walletTokens: TokenAccount[] | undefined,
  tokenAddress: string,
): {
  decimals: number
  balance: number
} => {
  if (!walletTokens?.length) return { balance: 0, decimals: 6 }
  const walletToken = walletTokens.find(
    (t) => t.mint.toString() === tokenAddress,
  )
  let balance = walletToken?.uiAmount || 0
  const decimals = walletToken?.decimals ?? 6
  if (tokenAddress === SOL_MINT && balance) {
    balance = balance > MIN_SOL_BALANCE ? balance - MIN_SOL_BALANCE : 0
  }

  return {
    balance,
    decimals,
  }
}

const fetchJupiterPriceForMint = async (mint: string) => {
  try {
    const response = await jupiterFetch(makeJupiterPriceUrlForMints([mint]))

    if (!response.ok) {
      console.error(
        `Jupiter price API returned non-200 status: ${toContextTuplesString({
          status: response.status,
          body: await response.text(),
          mint,
        })}`,
      )
      return 0
    }

    const jupiterPrice = (await response.json()) as JupiterPriceResponse
    return jupiterPrice?.data?.[mint]?.usdPrice
      ? parseFloat(jupiterPrice.data[mint].usdPrice.toString())
      : 0
  } catch (e) {
    console.error(`failed to fetch jupiter price: mint=${mint}`, e)
    return 0
  }
}
